import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { AxiosError } from 'axios'
import type { CreateGuideInput } from '@/types/guide'
import { GuidesService } from '@/services/guides'
import TitleHeader from '@/components/TitleHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/RichTextEditor'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/_authenticated/products/guides/create/')(
  {
    component: CreateGuide,
  },
)

function CreateGuide() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('vi')

  // Vietnamese fields
  const [viName, setViName] = useState('')
  const [viDescription, setViDescription] = useState('')

  // English fields
  const [enName, setEnName] = useState('')
  const [enDescription, setEnDescription] = useState('')

  const mutation = useMutation({
    mutationFn: (payload: CreateGuideInput) => GuidesService.create(payload),
    onSuccess: () => {
      toast.success('Thêm hướng dẫn thành công')
      navigate({ to: '/products/guides' })
    },
    onError: (error: AxiosError) => {
      toast.error(
        (error.response?.data as any)?.message || 'Thêm hướng dẫn thất bại',
      )
    },
  })

  const handleSubmit = () => {
    if (!viName || !viDescription) {
      toast.error('Vui lòng điền đầy đủ thông tin tiếng Việt')
      return
    }

    const translations: CreateGuideInput['translations'] = [
      {
        language: 'vi',
        name: viName,
        description: viDescription,
      },
    ]

    // Add English translation if provided
    if (enName || enDescription) {
      translations.push({
        language: 'en',
        name: enName || viName,
        description: enDescription || viDescription,
      })
    }

    mutation.mutate({
      code: `GUIDE_${Date.now()}`, // Auto-generate code
      translations,
    })
  }

  const handleCancel = () => {
    navigate({ to: '/products/guides' })
  }

  const isFormValid = viName && viDescription

  return (
    <div className="space-y-6">
      <TitleHeader
        title="Thêm hướng dẫn"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              Huỷ
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmit}
              disabled={mutation.isPending || !isFormValid}
            >
              {mutation.isPending ? 'Đang lưu...' : 'Lưu'}
            </Button>
          </div>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="vi">Tiếng Việt</TabsTrigger>
          <TabsTrigger value="en">English</TabsTrigger>
        </TabsList>

        <TabsContent value="vi" className="space-y-6">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Hướng dẫn</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="vi-name" className="mb-2 inline-block">
                    Tiêu đề hướng dẫn
                  </Label>
                  <Input
                    id="vi-name"
                    value={viName}
                    onChange={(e) => setViName(e.target.value)}
                    placeholder="Hướng dẫn game...."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Nội dung</CardTitle>
            </CardHeader>
            <CardContent>
              <RichTextEditor
                value={viDescription}
                onChange={setViDescription}
                placeholder="Nhập nội dung hướng dẫn..."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="en" className="space-y-6">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="en-name" className="mb-2 inline-block">
                    Guide Title
                  </Label>
                  <Input
                    id="en-name"
                    value={enName}
                    onChange={(e) => setEnName(e.target.value)}
                    placeholder="Game guide...."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Content</CardTitle>
            </CardHeader>
            <CardContent>
              <RichTextEditor
                value={enDescription}
                onChange={setEnDescription}
                placeholder="Enter guide content..."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
